import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../store/store";
import { IRankData, TBuyBoxAnalysis } from "./productAnalyzer.types";
import { ProfitCalculator } from "../../helpers";
import { TBuyBoxAnalysisTableRow } from "../../pages/ProductAnalyzer/partials/BuyBoxAnalysis";
import { TSellerOfferTableRow } from "../../pages/ProductAnalyzer/partials/Offers";
import { transformKeepaToBuyBoxAnalysis } from "../../utils/productAnalzyer/buyboxAnalysis";

export const selectProductDetail = createSelector(
  (state: RootState) => state.productAnalyzer.productDetail,
  (state: RootState) => state.productAnalyzer.loadingProductDetail,
  (productDetail, loadingProductDetail) => ({
    productDetail,
    loadingProductDetail,
  }),
);

export const selectOffers = createSelector(
  (state: RootState) => state.productAnalyzer.offers,
  (state: RootState) => state.productAnalyzer.loadingOffers,
  (state: RootState) => state.productAnalyzer.profitCalculator,
  (state: RootState) => state.productAnalyzer.feeRules,
  (state: RootState) => state.sellers.entities,
  (offers, loadingOffers, profitCalculator, feeRules, sellers) => {
    let data = offers || [];
    if (feeRules) {
      const calculator = new ProfitCalculator(profitCalculator, feeRules);

      data = data.map((offer) => ({
        ...offer,
        sellerName: sellers[offer.sellerId]?.name || "",
        rating: sellers[offer.sellerId]?.rating || 0,
        ratingCount: sellers[offer.sellerId]?.ratingCount || 0,
        profit: calculator.updatePriceAndCalculate(offer.price).profit,
        roi: calculator.updatePriceAndCalculate(offer.price).roi,
      }));
    }

    return {
      offers: data as TSellerOfferTableRow[],
      loadingOffers,
    };
  },
);

export const selectQuickInfo = createSelector(
  (state: RootState) => state.productAnalyzer.quickInfo,
  (state: RootState) => state.productAnalyzer.loadingQuickInfo,
  (quickInfo, loadingQuickInfo) => ({
    quickInfo,
    loadingQuickInfo,
  }),
);

export const selectRestrictions = createSelector(
  (state: RootState) => state.productAnalyzer.restrictions,
  (state: RootState) => state.productAnalyzer.loadingRestrictions,
  (restrictions, loadingRestrictions) => ({
    restrictions,
    loadingRestrictions,
  }),
);

export const selectAlerts = createSelector(
  (state: RootState) => state.productAnalyzer.alerts,
  (state: RootState) => state.productAnalyzer.loadingProductDetail,
  (alerts, loadingAlerts) => ({
    alerts,
    loadingAlerts,
  }),
);

export const selectRankAndPrice = createSelector(
  [
    (state: RootState) => state.productAnalyzer.ranksAndPrices,
    (state: RootState) => state.productAnalyzer.loadingRanks,
    (state: RootState) =>
      state.productAnalyzer.quickInfo?.categoryProductCount as number,
    (state: RootState, duration) => duration,
  ],
  (ranksAndPrices, loadingRanks, categoryProductCount, duration) => {
    const ranks = ranksAndPrices?.[duration as keyof IRankData];
    return {
      loadingRanks,
      ranks,
      categoryProductCount,
      netBBPriceChange: ranks?.netBuyBoxPriceChanges
        ? ranks.netBuyBoxPriceChanges.increased -
          ranks.netBuyBoxPriceChanges.decreased
        : 0,
    };
  },
);

export const selectProfitCalculator = createSelector(
  (state: RootState) => state.productAnalyzer.profitCalculator,
  (state: RootState) => state.productAnalyzer.feeRules,
  (profitCalculator, feeRules) => ({
    profitCalculator,
    feeRules,
  }),
);

export const selectPrices = createSelector(
  (state: RootState) => state.productAnalyzer.profitCalculator.salePrice,
  (state: RootState) => state.productAnalyzer.profitCalculator.costPrice,
  (salePrice, costPrice) => ({
    salePrice,
    costPrice,
  }),
);

export const selectProductNotes = createSelector(
  (state: RootState) => state.productAnalyzer.notes,
  (state: RootState) => state.productAnalyzer.loadingNotes,
  (notes, loadingNotes) => ({
    notes,
    loadingNotes,
  }),
);

export const selectSellerCentral = createSelector(
  (state: RootState) => state.productAnalyzer.sellerCentral,
  (sellerCentral) => ({
    sellerCentral,
  }),
);

export const selectBuyBoxAnalysis = createSelector(
  (state: RootState) => state.productAnalyzer.buyBoxSellerIdHistory,
  (state: RootState) => state.productAnalyzer.csv,
  (state: RootState) => state.productAnalyzer.offers,
  (state: RootState) => state.productAnalyzer.loadingBuyBox,
  (state: RootState) => state.sellers.entities,
  (state: RootState) => state.productAnalyzer.lastUpdate,
  (state: RootState, duration: number) => duration,
  (
    buyBoxSellerIdHistory,
    csv,
    offers,
    loadingBuyBox,
    sellers,
    lastUpdate,
    duration,
  ) => {
    let data = transformKeepaToBuyBoxAnalysis(
      {
        buyBoxSellerIdHistory,
        csv,
        offers: offers || [],
        lastUpdate,
      },
      duration,
    );
    data = data.map((row: TBuyBoxAnalysis) => ({
      ...row,
      sellerName: sellers[row.sellerId]?.name,
      rating: sellers[row.sellerId]?.rating,
      ratingCount: sellers[row.sellerId]?.ratingCount,
    }));
    return {
      buyBoxAnalysis: data as TBuyBoxAnalysisTableRow[],
      loadingBuyBox,
    };
  },
);

export const selectVariations = createSelector(
  (state: RootState) => state.productAnalyzer.variations,
  (state: RootState) => state.productAnalyzer.loadingProductDetail,
  (variations, isLoading) => ({
    variations,
    isLoading,
  }),
);

export const selectChartsData = createSelector(
  (state: RootState) => state.productAnalyzer.chartsData,
  (state: RootState) => state.productAnalyzer.loadingChartsData,
  (chartsData, loadingChartsData) => ({
    chartsData,
    loadingChartsData,
  }),
);

export const selectPriceChartRawData = createSelector(
  (state: RootState) => state.productAnalyzer.chartsData,
  (chartsData) => ({
    priceChartRawData: chartsData.slice(0, 6),
  }),
);

export const selectSalesChartRawData = createSelector(
  (state: RootState) => state.productAnalyzer.chartsData,
  (chartsData) => ({
    salesChartRawData: chartsData.slice(6, 7),
  }),
);

export const selectOffersChartRawData = createSelector(
  (state: RootState) => state.productAnalyzer.chartsData,
  (chartsData) => ({
    offersChartRawData: chartsData.slice(7),
  }),
);

export const selectKeepaData = createSelector(
  (state: RootState) => state.productAnalyzer.keepa,
  (state: RootState) => state.productAnalyzer.loadingKeepa,
  (keepa, loadingKeepa) => ({
    loadingKeepaData: loadingKeepa,
    keepaData: keepa,
  }),
);

export const selectAverageData = createSelector(
  (state: RootState) => state.productAnalyzer.averages,
  (state: RootState) => state.productAnalyzer.loadingAverages,
  (averages, loadingAverages) => ({
    averages,
    loadingAverages,
  }),
);

export const selectProductHistories = createSelector(
  (state: RootState) => state.productAnalyzer.histories,
  (state: RootState) => state.productAnalyzer.historyCount,
  (state: RootState) => state.productAnalyzer.loadingHistories,
  (histories, total, isLoading) => ({
    histories,
    total,
    isLoading,
  }),
);

export const selectProductTags = createSelector(
  (state: RootState) => state.productAnalyzer.tags,
  (tags) => tags,
);
